import { useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { format } from 'date-fns'
import { toast } from 'sonner'
import { Receipt } from 'lucide-react'

import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from '@/common/ui/dialog'
import { Button } from '@/common/ui/button'
import { Label } from '@/common/ui/label'
import { Textarea } from '@/common/ui/textarea'
import Spinner from '@/common/components/Spinner'
import showErrorToast from '@/common/utils/showErrorToast'

import useMutationCreatePrepayment from '../../api/hooks/useMutationCreatePrepayment'
import useQueryGetBillingDetails from '../../api/hooks/useQueryGetBillingDetails'
import { CryptoInvoiceQueryKeys } from '../../keys'
import { formatBillingMonth } from '../BillingDetailsSummary/constants'

interface CreatePrepaymentModalProps {
	isOpen: boolean
	onClose: () => void
}

const formatUSD = (value: number): string =>
	`$${value.toLocaleString('en-US', {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	})}`

const formatTHB = (value: number): string =>
	`฿${value.toLocaleString('th-TH', {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	})}`

export default function CreatePrepaymentModal({
	isOpen,
	onClose,
}: CreatePrepaymentModalProps) {
	const currentMonth = useMemo(() => format(new Date(), 'yyyy-MM'), [])
	const [note, setNote] = useState('')

	const queryClient = useQueryClient()
	const { mutateAsync: createPrepayment, isPending } =
		useMutationCreatePrepayment()
	const { data: billingData, isLoading: isBillingLoading } =
		useQueryGetBillingDetails({ billingMonth: currentMonth })

	const totalChargeUsd = billingData?.totalChargeUsd ?? 0
	const totalChargeThb = billingData?.totalChargeThb ?? 0
	const exchangeRate = billingData?.exchangeRate ?? 0

	const amount = Number(totalChargeUsd.toFixed(2))
	const canSubmit = !isBillingLoading && amount > 0

	const resetForm = () => {
		setNote('')
	}

	const handleClose = () => {
		if (isPending) return
		resetForm()
		onClose()
	}

	const handleConfirm = async () => {
		if (!canSubmit) {
			toast.error('ยังไม่มียอดค้างชำระสำหรับเดือนปัจจุบัน')
			return
		}

		try {
			await createPrepayment({
				amount,
				billingMonth: currentMonth,
				note: note.trim() || undefined,
			})

			toast.success('สร้างใบแจ้งหนี้ชำระล่วงหน้าสำเร็จ')

			queryClient.invalidateQueries({
				queryKey: [CryptoInvoiceQueryKeys.CryptoInvoices],
			})
			queryClient.invalidateQueries({
				queryKey: [CryptoInvoiceQueryKeys.BillingDetails],
			})

			resetForm()
			onClose()
		} catch (error) {
			showErrorToast(error as Error)
		}
	}

	return (
		<Dialog open={isOpen} onOpenChange={handleClose}>
			<DialogContent className="max-w-md">
				<DialogHeader>
					<DialogTitle>สร้างใบแจ้งหนี้ชำระล่วงหน้า</DialogTitle>
					<DialogDescription>
						ชำระค่าคอมมิชชั่นล่วงหน้าตามยอดค้างชำระปัจจุบันของเดือนนี้
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4">
					<div className="space-y-2">
						<Label>เดือนที่ชำระ</Label>
						<div className="rounded-md border bg-muted px-3 py-2 text-sm font-medium">
							{formatBillingMonth(currentMonth)}
						</div>
					</div>

					<div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-950/30">
						<div className="mb-2 flex items-center gap-2 text-sm font-medium text-blue-900 dark:text-blue-200">
							<Receipt className="h-4 w-4" />
							ยอดที่จะชำระล่วงหน้า (USD)
						</div>
						{isBillingLoading ? (
							<div className="flex items-center gap-2 text-sm text-muted-foreground">
								<Spinner />
								กำลังคำนวณ...
							</div>
						) : (
							<div className="space-y-1">
								<p className="text-3xl font-bold text-blue-600">
									{formatUSD(amount)}
								</p>
								<p className="text-xs text-muted-foreground">
									≈ {formatTHB(totalChargeThb)}
									{exchangeRate > 0 && (
										<> (อัตราแลกเปลี่ยน ฿{exchangeRate.toFixed(2)}/USD)</>
									)}
								</p>
							</div>
						)}
						<p className="mt-3 text-xs text-muted-foreground">
							* จำนวนเงินคำนวณจากยอดค้างชำระปัจจุบันโดยอัตโนมัติ
							ไม่สามารถแก้ไขได้
						</p>
					</div>

					<div className="space-y-2">
						<Label htmlFor="prepayment-note">หมายเหตุ (ไม่บังคับ)</Label>
						<Textarea
							id="prepayment-note"
							placeholder="ระบุรายละเอียดเพิ่มเติม..."
							value={note}
							onChange={(e) => setNote(e.target.value)}
							rows={3}
						/>
					</div>

					<div className="flex justify-end gap-2 pt-2">
						<Button
							variant="outline"
							onClick={handleClose}
							disabled={isPending}
						>
							ยกเลิก
						</Button>
						<Button onClick={handleConfirm} disabled={isPending || !canSubmit}>
							{isPending && <Spinner />}
							ยืนยันชำระล่วงหน้า
						</Button>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	)
}
